/**
 * Authors: mariushirjanu
 * Created: 06/05/2021
 */

import * as _ from 'underscore';
import { util } from '../../../shared/util';
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { IWebshopSupplierData, WebshopSupplierDataService } from './webshop-supplier-data.service';
import { FormattingService } from '../../../shared/services/formatting.service';
import { WebshopService } from '../webshop.service';
import { EVENT_CLOSE_DIALOG, GaEventService, IGAEvent } from '../../../shared/services/ga-event.service';
import { GaMessageService, IDialog } from '../../../shared/message/ga-message.service';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { ScrollService } from '../../../shared/scroll/scroll.service';
import { SEC_WS_BPACTIVATION, SEC_WS_BPSETUPCONDITIONS, SEC_WS_GEOMATCH, SEC_WS_INACTIVEBPS, SEC_WS_MANAGEMENT, SEC_WS_SERVICETICKET, SEC_WS_SUPPLIER } from '../../../shared/services/auth/security-objects';
import { TranslationService } from '../../../shared/translation/translation.service';
import { ResponsiveService, MaxWidth } from '../../../shared/responsive/responsive.service';
import { STATE_REPORT } from '../../../shared/app-states';
import { GA1Service } from '../../../shared/ga1/ga1.service';
import { Subscription } from 'rxjs';
import { FaqItemComponent } from '../../../shared/faq/faq-item.component';
import { ReportFiltersUtil } from '../../../shared/report/report-filters-util.service';
import { GaWizard } from '../../../shared/wizard/ga-wizard';
import { GaWizardService } from '../../../shared/wizard/ga-wizard.service';
import { WIZARD_WEBSHOP } from '../../../shared/app-wizards';
import { ReportWrapperService } from '../../../shared/report/report-wrapper.service';
import { TICKET_WORKFLOW_TYPE_WEBSHOP, ISimpleTicket } from '../../../shared/ticket/ticket-data.service';
import { FAQ_BP_ACTIVATION_LETTER } from '../../../shared/faq/faq-codes';

const SECTION_GEOMATCH:string = 'GEOMATCH';
const SECTION_SUPPLIER_SERVICES:string = 'SUPPLIER_SERVICES';

interface IFormlyFields {
  collapsed?:IExtraFieldConfigurationObject[];
  expanded?:IExtraFieldConfigurationObject[];
}

interface ISupplierReport {
  id:number;
  filters?:{ [ key:string ]:any };
  refParams?:{ [ key:string ]:any };
  storedReport?:string;
}

interface ICollapsibleSection {
  key:string;
  id?:string;
  fields?:IFormlyFields;
  isCollapsed?:boolean;
  showSection?:boolean;
  isLink?:boolean;
  report?:ISupplierReport;
  expanded?:{
    iframe?:{
      src:string;
      height?:number;
      scrolling?:boolean;
    };
    report?: ISupplierReport;
  };
  category?:string;
  screen?:string;
}

@Component({
  selector: 'webshop-supplier',
  template: `
    <div class="webshop-supplier">
      <h2>
        <text key="HEADING"></text>
      </h2>
      <div *ngIf="!loading">
        <div class="col-space-bottom" *ngFor="let section of formSections"> <!-- repeat the sections -->
          <div *ngIf="section.showSection" [id]="section.id">
            <editable-text [key]="section.key" [category]="section.category" [screen]="section.screen" menu="title" highlight="h3 a:eq(0)"></editable-text>
            <h3 *ngIf="section.isLink; else collapsible">
              <a uiSref="${STATE_REPORT}" [uiParams]="section.report.refParams" target="_blank">
                {{'WEBSHOP.SUPPLIER.' + section.key + '.TITLE' | translate}}
                <span class="detailed-report-link"><i class="fa fa-table"></i></span>
              </a>
            </h3>
            <ng-template #collapsible>
              <collapsible-pane class="pane-detail" title="{{ 'WEBSHOP.SUPPLIER.' + section.key + '.TITLE' | translate }}" [collapsed]="section.isCollapsed" (onChange)="onSectionCollapsed(section, $event)">
                <div class="collapsible-title-extra" *ngIf="section?.expanded?.report?.id"> <!-- content projection: collapsible-title-extra -->
                  <span class="detailed-report-link"><a uiSref="${STATE_REPORT}" [uiParams]="section.expanded.report.refParams" target="_blank"><i class="fa fa-table"></i></a></span>
                </div>
                <div class="form-horizontal collapse-section"> <!-- expanded fields -->
                  <formly *ngIf="section.fields?.expanded" [form]="form" [fields]="section.fields.expanded" [model]="model"></formly>
                  <iframe-container [name]="section.key" [src]="section?.expanded?.iframe?.src" [height]="section?.expanded?.iframe?.height" [scrolling]="section?.expanded?.iframe?.scrolling"></iframe-container>
                  <report-wrapper *ngIf="section.expanded?.report?.id && !section?.expanded?.iframe?.src" [reportId]="section.expanded.report.id" [filters]="section.expanded.report.filters" [autoload]="false" [storedReport]="section.expanded.report.storedReport" gridHeight="100px;.ui-layout-north;.ui-layout-resizer-north"></report-wrapper>
                </div>
              </collapsible-pane>
              <collapsible-pane class="pane-summary" [collapsed]="!section.isCollapsed">
                <div *ngIf="section.isCollapsed" class="form-horizontal collapse-section"> <!-- collapsed fields -->
                  <formly *ngIf="section.fields?.collapsed" [form]="form" [fields]="section.fields.collapsed" [model]="model"></formly>
                </div>
              </collapsible-pane>
            </ng-template>
          </div>
        </div>
      </div>
      <h3 *ngIf="loading">
        <activity-indicator useDefaultText="true"></activity-indicator>
      </h3>
    </div>
  `,
  styleUrls: [ './webshop-supplier.scss' ],
  encapsulation: ViewEncapsulation.None
})
export class WebshopSupplierComponent implements OnInit, OnDestroy {
  @Input() public menu:string;

  public formSections:ICollapsibleSection[];
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public model:IWebshopSupplierData;
  public loading:boolean;

  private wizard:GaWizard;
  private eventHandler:Subscription;
  private webshopId:number;
  public showFaqs:boolean;

  private hasWritePermission:boolean;

  private canUpdateGeomatch:boolean;
  private canUpdateServiceTicket:boolean;
  private canUpdateSupplierManagement:boolean;
  private canViewBPActivation:boolean;
  private canViewBPSetupConditions:boolean;
  private canViewInactiveBPs:boolean;

  private webshopSetupTicketLink:IExtraFieldConfigurationObject;
  private editWebshopTicketDialog:IDialog;
  private newWebshopTicketDialog:IDialog;

  constructor(
    private authService:AuthService,
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private ga1Service:GA1Service,
    private gaEventService:GaEventService,
    private messageService:GaMessageService,
    private reportFiltersUtil:ReportFiltersUtil,
    private reportWrapperService: ReportWrapperService,
    private responsiveService:ResponsiveService,
    private scrollService:ScrollService,
    private translationService:TranslationService,
    private webshopSupplierDataService:WebshopSupplierDataService,
    private webshopService:WebshopService,
    wizardService:GaWizardService
  ) {
    this.responsiveService.width = MaxWidth.LG;
    this.wizard = wizardService.getInstance(WIZARD_WEBSHOP);
    this.wizard.clearActions(); // no buttons in Webshop supplier as they are under sections
    this.webshopId = this.webshopService.getWebshopId();
  }

  public ngOnInit():void {
    // get webshop supplier permission
    this.getPermissions();
    this.showFaqs = true;

    // initial webshop supplier data
    this.loading = true;
    this.webshopSupplierDataService.getWebshopSupplierData(this.webshopService.getWebshopId())
      .then((data:IWebshopSupplierData) => {
        // init formly model
        this.model = data;

        this.initSections();
        this.listen();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private getPermissions():void {
    this.hasWritePermission = this.authService.viewWrite(SEC_WS_SUPPLIER);
    this.canUpdateGeomatch = this.authService.viewWrite(SEC_WS_GEOMATCH);
    this.canUpdateServiceTicket = this.authService.viewWrite(SEC_WS_SERVICETICKET);
    this.canUpdateSupplierManagement = this.authService.viewWrite(SEC_WS_MANAGEMENT);
    this.canViewBPActivation = this.authService.viewWrite(SEC_WS_BPACTIVATION);
    this.canViewBPSetupConditions = this.authService.viewWrite(SEC_WS_BPSETUPCONDITIONS);
    this.canViewInactiveBPs = this.authService.viewWrite(SEC_WS_INACTIVEBPS);
  }

  private initSections():void {
    this.formSections = [
      {
        key: SECTION_GEOMATCH,
        fields: this.createGeomatchFields(),
        isCollapsed: true,
        showSection: this.canUpdateGeomatch,
        expanded: {
          iframe: {
            src: `/unacc/WP_GeoView_Quick.jsp?Desprid=${this.webshopId}&ejbEvent=create&browseList=true&QuickList=1&RealejbEvent=Edit`,
            height: 450
          }
        }
      },
      {
        key: SECTION_SUPPLIER_SERVICES,
        fields: this.createSupplierServicesFields(),
        isCollapsed: true
      },
      {
        key: 'SUPPLIER_MANAGEMENT',
        isLink: true,
        showSection: this.canUpdateSupplierManagement,
        report: {
          id: 120,
          filters: {
            Desprnm: this.webshopId
          },
          refParams: {
            autoload: 'true'
          }
        }
      },
      {
        key: 'BP_ACTIVATION',
        isCollapsed: true,
        showSection: this.canViewBPActivation,
        expanded: {
          report: {
            id: 120,
            filters: {
              Desprnm: this.webshopId
            }
          }
        }
      },
      {
        key: 'BP_CAVEATS',
        isCollapsed: true,
        showSection: true,
        expanded: {
          iframe: {
            src: `/unacc/WP_CaveatsView_Edit.jsp?Desprid=${this.webshopId}&browseList=true&ejbEvent=create&RealejbEvent=Edit&wv_Detitle=Supplier%20Caveats%20%2F%20Order%20Conditions`,
            height: 700,
            scrolling: true
          },
          report: { // only used for the link to report
            id: 260,
            filters: {
              Desprid: this.webshopId,
              Type1_description: 'PO_CONDITIONS',
              Deagree_ds: 'YES'
            }
          }
        }
      },
      {
        key: 'BP_SETUP',
        isCollapsed: true,
        showSection: this.canViewBPSetupConditions,
        expanded: {
          report: {
            id: 260,
            filters: {
              Desprid: this.webshopId,
              Type1_description: 'SETUP_CONDITIONS'
            }
          }
        }
      },
      {
        key: 'BP_PROFILES',
        isCollapsed: true,
        showSection: true,
        expanded: {
          report: {
            id: 74,
            filters: {
              Desprid: this.webshopId
            },
            storedReport: 'SUPPLIER_ACTIVATION'
          }
        }
      },
      {
        key: 'BP_INACTIVE',
        isCollapsed: true,
        showSection: this.canViewInactiveBPs,
        expanded: {
          report: {
            id: 55,
            filters: {
              Desprnm: this.webshopId,
              Deinact_ds: 0,
              Decntpo: 0
            }
          }
        }
      }
    ];

    _.each(this.formSections, (section, i) => {
      // calling getReportSrefParams() in template will result in a very bad performance (no one time binding), therefore define here refParams
      if (section.isLink && section.report?.id) {
        this.formSections[i].report.refParams = this.getReportSrefParams(section.report);
      }
      if (section?.expanded?.report?.id) {
        this.formSections[ i ].expanded.report.refParams = this.getReportSrefParams(section.expanded.report);
      }

      // non-configured section are default shown
      if (util.isNullOrUndefined(this.formSections[ i ].showSection)) {
        this.formSections[ i ].showSection = true;
      }

      // add sections ids based on section's key
      this.formSections[ i ].id = this.getSectionId(section.key);
    });
  }

  private getSection(key:string):ICollapsibleSection {
    return _.findWhere(this.formSections, { key });
  }

  private listen():void {
    // subscriber to close GA1 dialogs
    this.eventHandler = this.gaEventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_CLOSE_DIALOG) {
        // close dialogs
        this.editWebshopTicketDialog?.hide();
        this.newWebshopTicketDialog?.hide();

        this.reloadAllSectionReports();

        this.webshopSupplierDataService.getWebshopSupplierData(this.webshopService.getWebshopId())
          .then((data:IWebshopSupplierData) => {
            this.model = data;

            // update the counter for countries selected in Geomatch section
            this.getSection(SECTION_GEOMATCH).fields = this.createGeomatchFields();

            // update webshop setup ticket link
            this.webshopSetupTicketLink.templateOptions.label = this.formatTicket();
          });
      }
    });
  }

  private createGeomatchFields():IFormlyFields {
    const fields:IFormlyFields = {};

    fields.collapsed = [
      this.formlyService.aTemplate(this.translationService.instant('NUM_ACTIVE_COUNTRIES', { total: this.model.numActiveCountries }))
        .build()
    ];

    return fields;
  }

  private createSupplierServicesFields():IFormlyFields {
    const labelClass:string = 'col-md-4';
    const valueClass:string = 'col-md-8';
    const fields:IFormlyFields = {};

    const customerPreferredSupplierInfo:IExtraFieldConfigurationObject = this.formlyService.aLink('WEBSHOP.SUPPLIER.CUSTOMERPREFERREDSUPPLIER.LINK')
      .withoutWrapper()
      .withWrapperClass('col-12 info-link')
      .onClick(() => {
        this.messageService.showDialog({
          component: FaqItemComponent,
          inputs: {
            faqCode: FAQ_BP_ACTIVATION_LETTER
          }
        });
      })
      .build();


    const webshopSetupTicketGroup:IExtraFieldConfigurationObject = this.formlyService.aGroup('setupTicket')
      .withWrapperClass('margin-bottom-sm')
      .withLabelClass('col-md-12')
      .withValueClass('col-md-12')
      .withFields([
        this.webshopSetupTicketLink = this.formlyService.aLink(this.formatTicket())
          .withoutWrapper()
          .withWrapperClass('col-md-12')
          .withLabelClass('d-none')
          .onClick(() => {
            const ga1Url:string = this.ga1Service.getGA1Url(GA1Service.URLTYPE_SUPPORT_TICKET, {
              row0_Demndid: this.model?.webshopSetupTicket?.id,
              browseList: true, // needed for closing the dialog
              ejbEvent: 'edit'
            }, { frameless: true });
            this.editWebshopTicketDialog = this.messageService.showGA1Dialog('WEBSHOP.SUPPLIER.SETUPTICKET.LABEL', ga1Url);
          })
          .withHideExpression(() => !this.model.webshopSetupTicket?.id || !this.model.webshopSetupTicket?.accessible)
          .build(),
        this.formlyService.aValue('webshopSetupTicket')
          .withoutWrapper()
          .withWrapperClass('col-md-12')
          .withFormat(this.formatTicket)
          .withHideExpression(() => !this.model.webshopSetupTicket?.id || this.model.webshopSetupTicket?.accessible)
          .build(),

        this.formlyService.aTemplate(`<div>${this.translationService.instant('NO_TICKET')}</div>`)
          .withHideExpression(() => !!this.model.webshopSetupTicket?.id)
          .build(),

        this.formlyService.aLink('WEBSHOP.SUPPLIER.NEW_SETUP_TICKET.TEXT')
          .withoutWrapper()
          .withWrapperClass('col-md-12')
          .onClick(() => {
            const ga1Url:string = this.ga1Service.getGA1Url(GA1Service.URLTYPE_SUPPORT_TICKET, {
              Desprid: this.webshopId,
              Dewflty: TICKET_WORKFLOW_TYPE_WEBSHOP,
              Demndst: 'OPEN',
              Demntty: 'SETUP',
              ejbEvent: 'create',
              browseList: true // needed for closing the dialog
            }, { frameless: true });
            this.newWebshopTicketDialog = this.messageService.showGA1Dialog('WEBSHOP.SUPPLIER.NEW_SETUP_TICKET.TEXT', ga1Url);
          })
          .withExtraOptions({
            templateOptions: {
              linkClass: 'btn-primary'
            }
          })
          .withHideExpression(() => this.getSection(SECTION_SUPPLIER_SERVICES).isCollapsed || !this.canUpdateServiceTicket || !!this.model.webshopSetupTicket?.id)
          .build()
      ])
      .build();

    fields.collapsed = [
      webshopSetupTicketGroup,

      this.formlyService.aValue('regionalHub')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withInfo()
        .showInfo()
        .withoutInfoOffset()
        .build(),

      this.formlyService.aValue('hybridFulfilmentModel')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withInfo()
        .showInfo()
        .withoutInfoOffset()
        .build(),

      this.formlyService.aValue('customerPreferredSupplier')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      customerPreferredSupplierInfo,

      this.formlyService.aValue('multipleCountrySuppliers')
        .withFormat(this.formattingService.formatBoolean)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withInfo()
        .showInfo()
        .withoutInfoOffset()
        .build()
    ];

    fields.expanded = [
      webshopSetupTicketGroup,

      this.formlyService.aCheckbox('regionalHub')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withInfo()
        .showInfo()
        .withoutInfoOffset()
        .build(),

      this.formlyService.aCheckbox('hybridFulfilmentModel')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withInfo()
        .showInfo()
        .withoutInfoOffset()
        .build(),

      this.formlyService.aCheckbox('customerPreferredSupplier')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      customerPreferredSupplierInfo,

      this.formlyService.aCheckbox('multipleCountrySuppliers')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withInfo()
        .showInfo()
        .withoutInfoOffset()
        .build(),


      this.formlyService.aLink(this.translationService.instant('GA.BUTTONS.SAVE_LABEL', { label: 'WEBSHOP.SUPPLIER.SUPPLIER_SERVICES.TITLE' }))
        .withoutWrapper()
        .onClick(this.saveWebshopSupplierForm)
        .withExtraOptions({
          templateOptions: {
            linkClass: 'btn-success'
          }
        })
        .withHideExpression(() => !this.hasWritePermission)
        .build()
    ];

    return fields;
  }

  public onSectionCollapsed(section:ICollapsibleSection, collapsed:boolean):void {
    section.isCollapsed = collapsed;
    if (!collapsed) {

      // load the report when after the section has been expanded
      if (section?.expanded?.report?.id) {
        this.reportWrapperService.loadReportData(section.expanded.report.id);
      }

      setTimeout(() => {
        this.scrollService.scrollToElement(`#${this.getSectionId(section.key)}`);
      }, 500);
    }
  }

  public saveWebshopSupplierForm = ():void => {
    this.webshopSupplierDataService.saveWebshopSupplierData(this.webshopId, this.model)
      .then((response:IWebshopSupplierData) => {
        this.model = response;
      });
  };

  public getReportSrefParams(report:ISupplierReport):{ [ key:string ]:any } {
    const refParams:any = {
      reportId: report.id,
      filters: this.reportFiltersUtil.filtersToParam(report.filters),
      showfilters: true,
      autoload: true
    };

    if (report.storedReport) {
      refParams.storedReport = report.storedReport;
    }

    return refParams;
  }

  public getSectionId(key:string):string {
    return key && `section-${key.replace('_', '-').toLowerCase()}`;
  }

  private reloadAllSectionReports():void {
    _.each(this.formSections, (formSection:ICollapsibleSection) => {
      if (formSection.expanded?.report?.id) {
        this.reportWrapperService.loadReportData(formSection.expanded.report.id, true);
      }
    });
  }

  private formatTicket = (ticket?:ISimpleTicket):string => {
    ticket = ticket || this.model.webshopSetupTicket;
    return `${ticket?.id} ${ticket?.description} (${ticket?.status.toUpperCase()})`;
  };

  public ngOnDestroy():void {
    this.responsiveService.width = MaxWidth.SM; // revert max width for other screens
    this.wizard.clearActions();
    // unsubscribe from events
    this.eventHandler.unsubscribe();
  }
}